import React, { useEffect, useMemo, useState } from 'react';

import { Button, Divider, RadioGroup, Switch, TextInput, withPage } from '@sms/plasma-ui';

import {
  EventList,
  EventCardItem,
  EventView,
  EventFilter,
  FilterState,
  eventApi,
  Event,
  IEventFilter,
} from '@sms/journal-event-journal-event-ui';

import * as styled from './TestEmbeddedPage.styles';

enum Type {
  Iframe = 'Iframe',
  List = 'List',
  View = 'View',
  Item = 'Item',
  Filter = 'Filter',
}

function TestEmbeddedPage() {
  const [type, setType] = useState<Type>(Type.Iframe);
  const [inputUrl, setInputUrl] = React.useState<string>();
  const [url, setUrl] = React.useState(inputUrl);
  const [events, setEvents] = useState<Event[]>([]);
  const [filter, setFilter] = useState<FilterState>({ period: { Start: new Date(2025, 0, 1), End: new Date(2027, 0, 1) } });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [expandAll, setExpandAll] = useState(true);
  const [hideAttachmentsTab, setHideAttachmentsTab] = useState(true);
  const [hideCommentsTab, setHideCommentsTab] = useState(true);
  const [hideLinksTab, setHideLinksTab] = useState(true);
  const [showReactionsCount, setShowReactionsCount] = useState(true);

  const loadEvents = async (page: number) => {
    setLoading(true);
    const eventFilter: IEventFilter = {
      start: filter.period.Start ?? new Date(2025, 0, 1),
      finish: filter.period.End ?? new Date(2027, 0, 1),
      page: page,
      pageSize: 30,
    };
    const result = await eventApi.getEvents(eventFilter);

    setEvents(prev => [...prev, ...result]);
    setHasMore(result.length === 30);
    setLoading(false);
  }

  const loadNextPage = () => {
    if (hasMore && !loading)
      setPage(prev => prev + 1);
  };

  const eventWithReactions = useMemo(() => events.find(e => e.ReactionCount), [events]);

  useEffect(() => {
    loadEvents(page);
  }, [page]);

  return (
    <styled.TestEmbeddedPage>
      <styled.MenuContainer>
        <RadioGroup
          name='radio'
          label='Component'
          dataSource={[
            'Iframe',
            'List',
            'View',
            'Item',
            'Filter',
          ].map(item => ({ label: item, value: item }))}
          value={type}
          onChange={e => setType(e.target.value)}
        />

        {
          type === Type.Iframe &&
          <div className="url-input">
            <TextInput name="url" label='URL' onChange={(v) => setInputUrl(v.target.value)} value={inputUrl} />
            <Button type="primary" title="Load" onClick={() => setUrl(inputUrl)} />
          </div>
        }

        {type === Type.View &&
          <>
            <Switch name='hideCommentsTab' label="Hide comments tab" checked={hideCommentsTab} onChange={setHideCommentsTab} />
            <Switch name='hideAttachmentsTab' label="Hide attachments tab" checked={hideAttachmentsTab} onChange={setHideAttachmentsTab} />
            <Switch name='hideLinksTab' label="Hide links tab" checked={hideLinksTab} onChange={setHideLinksTab} />
          </>
        }

        {(type === Type.Item || type === Type.List) &&
          <Switch name='showReactionsCount' label="Show reactions count" checked={showReactionsCount} onChange={setShowReactionsCount} />
        }
      </styled.MenuContainer>

      <Divider style={{ margin: 0 }} />

      <styled.ContentContainer>
        {
          type === Type.Iframe &&
          <styled.IframeContainer>
            <iframe src={url} />
          </styled.IframeContainer>
        }

        {type === Type.Filter &&
          <EventFilter
            filter={filter}
            onChangeFilter={filter => { setFilter(filter); console.log({ filter }); }}
            onRefresh={() => console.log('refresh')}
            expand={expandAll}
            onChangeExpand={expand => { setExpandAll(expand); console.log({ expand }); }}
          >
            <p>Sample</p>
            <p>
              Lorem ipsum dolor sit amet, consectetur adipiscing elit. Proin enim felis, molestie eu efficitur et, sodales id risus. Curabitur sed eros id nibh hendrerit semper. Suspendisse fermentum suscipit quam, non mollis enim. Nulla facilisi. Sed dui nisl, pellentesque eget nisl et, maximus posuere metus. Duis nec justo sapien. Aliquam ultrices mauris in consequat posuere. Nullam auctor, diam non suscipit varius, ligula diam elementum diam, sed lobortis felis arcu vel elit. Sed posuere dignissim faucibus. Duis tempus ante ex, a elementum dui scelerisque ac. Nulla at condimentum neque, eget euismod quam. Aliquam volutpat eros ornare quam molestie, id convallis velit consequat.
            </p>
            <p>
              Fusce a molestie diam, quis tincidunt felis. Sed in hendrerit lectus. Sed maximus quis ligula et ultricies. Vestibulum ante ipsum primis in faucibus orci luctus et ultrices posuere cubilia curae; Nulla vitae vulputate nisl. Etiam eu euismod nibh, eu tincidunt dolor. Sed orci diam, tincidunt eget metus eu, convallis pharetra ipsum. Nam finibus mauris quis dolor pulvinar, vel dignissim sapien feugiat. Etiam sit amet rhoncus massa. Nulla vel laoreet ex. Aliquam quis molestie augue, sit amet varius nisi.
            </p>
            <p>
              Duis vel leo porta, suscipit nisi eu, malesuada erat. Donec sit amet diam ut purus aliquet cursus. Praesent placerat ut arcu at vehicula. Vestibulum et lectus sed felis efficitur finibus. Aliquam mattis sed neque sit amet imperdiet. Duis ac ex et sem pellentesque gravida. Proin consequat dolor justo, a venenatis mi sodales vel. Donec volutpat eu risus id fringilla. Duis mollis, turpis a finibus facilisis, mauris dui laoreet urna, ut faucibus ipsum leo et ipsum. Mauris ullamcorper ex eget lorem sodales, ac rhoncus mi sodales. Morbi tincidunt nunc quis est rutrum vestibulum. Suspendisse porta neque nec turpis aliquam commodo. Vivamus libero turpis, convallis ut est vitae, posuere efficitur ipsum.
            </p>
            <p>
              Ut a tincidunt tortor. Praesent non enim mi. Etiam gravida pharetra odio. Phasellus nec gravida libero. Cras pellentesque aliquam rutrum. Phasellus ornare leo id arcu fermentum, a volutpat lacus interdum. Suspendisse egestas lectus porttitor libero bibendum, vel interdum risus porta. Vivamus pretium varius lorem, nec aliquam nunc facilisis sit amet. Aenean ut dolor ut velit dictum dignissim vitae suscipit leo. Suspendisse metus nisl, varius vitae nibh at, pretium egestas dui. Suspendisse consectetur fermentum faucibus. Pellentesque ullamcorper metus vitae leo congue, eu mattis odio euismod. Cras gravida lorem eros, ut pretium magna accumsan at. Ut porttitor commodo odio, hendrerit rutrum nisl rutrum nec. Morbi tempor enim ornare lobortis mattis.
            </p>
          </EventFilter>
        }

        {type === Type.View &&
          events.length > 0 &&
          <EventView
            event={eventWithReactions || events[0]}
            hideAttachmentsTab={hideAttachmentsTab}
            hideCommentsTab={hideCommentsTab}
            hideLinksTab={hideLinksTab}
          />
        }

        {type === Type.Item &&
          events.length > 0 &&
          <EventCardItem
            event={events[0]}
            onClick={() => console.log('click event')}
            showReactionsCount={showReactionsCount}
            extra={
              <>
                <p>Sample</p>
                <p>
                  Lorem ipsum dolor sit amet, consectetur adipiscing elit. Proin enim felis, molestie eu efficitur et, sodales id risus.
                </p>
              </>
            }
          />
        }

        {type === Type.List &&
          <EventList
            dataSource={events}
            loading={loading}
            onLoadNextPage={loadNextPage}
            height="calc(100vh - 49px)"
            showReactionsCount={showReactionsCount}
          />
        }
      </styled.ContentContainer>
    </styled.TestEmbeddedPage>
  );
}

export default withPage(TestEmbeddedPage);
